import { AnimalOrganSystem, CerberusIntegrationStatus } from 'types/types';
import { ConsignmentCirculationObjectStatus, ConsignmentOrigin } from 'modules/consignments/types';
import { PermitObjectOperation, PermitObjectStatus } from 'modules/pharmPermissions/types';
import { GmpObjectOperation, GmpObjectStatus } from 'modules/gmp/types';

export type CountryDictOption = {
  guid: string;
  name: null | string;
};

export type RegionDictOption = {
  guid: string;
  view: null | string;
};

export type DocumentDictOption = CommonDictOption;

export type CommonDictOption = {
  id: string;
  name: string;
};

export type ConsiderationPeriodDictOption = CommonDictOption & {
  code: string;
};

export type RegistryOperationDictOption = CommonDictOption & {
  code: string;
  productType: string;
  registryStatus: StatusesDictOption;
};

export type PharmReleaseDictOption = ConsiderationPeriodDictOption;
export type PharmPackageDictOption = ConsiderationPeriodDictOption;

export type GmoResearchTypeDictOption = {
  code: string;
  description: string;
  id: string;
  uuid: string;
  view: string;
};

export type GmoUseTypeDictOption = {
  code: string;
  description: string;
  id: string;
  uuid: string;
  view: string;
};

export type FeedAdditiveDictOption = {
  code: string;
  description: string;
  id: string;
  uuid: string;
  view: string;
};

export enum DictOptionStatusType {
  'RETURN',
  'DONE',
  'FAILURE',
  null,
}

export type StatusesDictOption = {
  code: number;
  codeGMO?: number | null;
  name: string;
  description?: string;
  forGMO?: boolean;
  requestState?: boolean;
  registryState?: boolean;
  inactiveState?: boolean;
  inVGNKI?: boolean;
  initial?: boolean;
  deleted?: boolean;
  statusType?: DictOptionStatusType;
  id?: string;
  view?: string;
};

export type AnimalSpeciesDictOption = {
  id: string;
  uuid: string;
  name?: string;
};

export type BooleanDictOption = {
  value: boolean;
  label: string;
};

export type DictOption = {
  label: string;
  value: string;
};

export type SigningPersonDictOption = {
  id: string;
  uuid: string;
  post: string;
  fio: string;
};

export enum StatusType {
  PETITION_STATUS = 'PETITION_STATUS',
  PETITION_REVIEW_STATUS = 'PETITION_REVIEW_STATUS',
}

export type PetitionStatusOption = BaseDictOption & { statusType: StatusType };

export type BaseDictOption = CommonDictOption & {
  code: string;
  text: string | null;
};

export type LanguageDictOption = CommonDictOption & {
  code?: string;
  codeAlfa2: string;
  codeAlfa3: string;
  codeAlfa3Rus?: string;
};

export type SymptomDictOption = CommonDictOption & {
  uuid: string;
  serious: boolean;
  animalOrganSystems: AnimalOrganSystem[];
};

export type SymptomDictOptionFront = {
  id?: string;
  uuid?: string;
  name: string;
  serious: DictOption;
  animalOrganSystems: { label: string; value: AnimalOrganSystem }[];
};

export type RoleTypeDictOption = Omit<CommonDictOption, 'id'> & {
  id: RoleTypeId;
  code: string;
};

export type ActivityKindOption = CommonDictOption & {
  roleType: RoleTypeDictOption | null;
};

export type ActivityKindOptionFront = CommonDictOption & {
  roleType: { label: string; value: RoleTypeDictOption } | null;
};

export type MeasureDictOption = {
  id: string;
  shortName: string;
  sysName: string;
};

export type ContactTypeDictOption = CommonDictOption & {
  code: ContactTypeCodes;
};

export type ConsignmentOriginDictOption = CommonDictOption & {
  sysName: ConsignmentOrigin;
  isPharmSubstance: boolean;
  isPharmaceutical: boolean;
};

export type ActiveSubstancesOption = {
  id?: string;
  name: string;
  code: string;
  extension?: number;
};

export type SystemParameterValueTypeOption = CommonDictOption & {
  code: SystemParameterValueTypeCodes;
};

export type OrgLegalFormDTO = {
  id: string;
  code: string;
  name: string;
  view?: string;
  type?: OrgLegalFormDescriptor;
  section?: OrgLegalFormDescriptor;
  group?: OrgLegalFormDescriptor;
  subGroup?: OrgLegalFormDescriptor;
};

export type OrgLegalFormDescriptor = {
  code: string;
  name: string;
};

export enum BaseDictCodes {
  PHARM_USE_CONDITION = 'PHARM_USE_CONDITION',
  CLINICAL_CONDITION = 'CLINICAL_CONDITION',
  CLINICAL_ACTION = 'CLINICAL_ACTION',
  REACTION_OUTCOME = 'REACTION_OUTCOME',
  REACTION_INFORMER = 'REACTION_INFORMER',
  PETITION_SOURCE = 'PETITION_SOURCE',
  VGNKI_REVIEW_RESULT = 'VGNKI_REVIEW_RESULT',
  PETITION_STATUS = 'PETITION_STATUS',
  PHARM_USE_TIME = 'PHARM_USE_TIME',
  PHARM_USE_FREQUENCY = 'PHARM_USE_FREQUENCY',
  PHARM_USE_DURATION = 'PHARM_USE_DURATION',
  PRESCRIPTION_STATUS = 'PRESCRIPTION_STATUS',
  PARAMETER_GROUP = 'PARAMETER_GROUP',
  REACTION_TYPE = 'REACTION_TYPE',
  SERVICE_REQUEST_SYSTEM = 'SERVICE_REQUEST_SYSTEM',
  SERVICE_REQUEST_TYPE = 'SERVICE_REQUEST_TYPE',
  SERVICE_REQUEST_STATUS = 'SERVICE_REQUEST_STATUS',
  REQUEST_METHOD = 'REQUEST_METHOD',
  ADDRESS_TYPE = 'ADDRESS_TYPE',
  CONTACT_TYPE = 'CONTACT_TYPE',
  EDUCATION_TYPE = 'EDUCATION_TYPE',
  LANGUAGE = 'LANGUAGE',
  DOSAGE_FORM = 'DOSAGE_FORM',
  DOSAGE_FORM_MAIN = 'DOSAGE_FORM_MAIN',
  UNITS_DOSAGE = 'UNITS_DOSAGE',
  UNITS_PRODUCT_COMPOSITION = 'UNITS_PRODUCT_COMPOSITION',
  PACKING_TYPE = 'PACKING_TYPE',
  OBJECT_TYPE = 'OBJECT_TYPE',
  BUSINESS_ENTITY_ADDRESS_TYPE = 'BUSINESS_ENTITY_ADDRESS_TYPE',
  ROLE_TYPE = 'ROLE_TYPE',
}

export enum MeasureUnitsCodes {
  FOR_PRESCRIPTION_DOSAGE = 'isForPrescriptionDosage',
  FOR_PRESCRIPTION = 'isForPrescription',
  FOR_ACTIVE_SUBSTANCE = 'isForActiveSubstance',
  FOR_PHARM_PERMISSION = 'isForPharmPermission',
}

export enum ContactTypeCodes {
  FAX = 'FX',
  PHONE = 'TE',
  EMAIL = 'EM',
}

export enum SystemParameterValueTypeCodes {
  FILE = 'FILE',
  STRING = 'STRING',
}

export enum RoleType {
  CONSUMER = 'CONSUMER',
  CLINIC = 'CLINIC',
  DISTRIBUTOR = 'DISTRIBUTOR',
}

export enum RoleTypeId {
  CLINIC = 'a16982c7-1033-4470-9496-86a63d7de8b6',
  CONSUMER = 'c075ddb0-a31e-4de1-9586-eb60aa5d7c72',
  DISTRIBUTOR = 'd2862336-72d7-4ce6-9aef-276714e45309',
  VENDOR = '6d3f2916-b3ce-42b6-94e8-64f89e4fc08f',
  PRODUCER = '033cc9bd-77ff-4e6c-b5c3-f3794fe6b72a',
  SUPPLIER = 'a5799920-76a4-4628-85f7-995fb856b08c',
  HOLDER = '80f38731-08a7-403b-af73-b957d1899225',
  REPORTER = 'a842da98-3b19-43e8-a4c1-7cf741f9b7a9',
}

export enum RegistryType {
  REQUEST = 'REQUEST',
  PERMIT = 'PERMIT',
}

export enum ObjectTypeId {
  PHARM_PERMISSION = '8295f668-b427-43f0-8804-80aca6d3b40f',
  USER_REGISTRATION_REQUEST = '353719bc-da91-4d47-9f03-10c95fa51cd1',
  GMP_COMPLIANCE_CONCLUSION = 'e8ebc4c0-0ba4-4387-9b0a-c579181b2499',
  CONSIGNMENT = 'ab3bccf1-c209-40f8-b85e-f894ada4cdb1',
  CONSIGNMENT_CIRCULATION = '390d8464-8769-4075-b25a-3101ab95b876',
  BUSINESS_ENTITY = 'f86bef2a-74cb-4fe1-b2ae-c4af3088db3d',
  INSPECTOR = '758a58ae-7949-4ea1-b260-c20e82dd1ab0',
  FAKE = '8efdcb44-f40e-4939-b1fa-0446285db0a7',
  ADVERSE_DRUG_PETITION = 'd97727c2-0af1-468b-9eaa-d63874645f63',
}

export enum CirculationStatusId {
  /** Свободное обращение */
  FREE = 'c1da22ba-43c5-4878-99ef-65de4e6738fb',
  /** Обращение препарата приостановлено */
  PHARM_SUSPENDED = '126c91df-0bd0-4998-926c-6df37d09cef8',
  /** Обращение серии приостановлено */
  BATCH_SUSPENDED = '2e5335d0-a8db-4535-b7d9-a8278f7f7a55',
  /** Препарат изъят из обращения */
  PHARM_WITHDRAWN = '90aaab0e-90d3-4aba-ae5a-414f84248e9c',
  /** Серия изъята из обращения */
  BATCH_WITHDRAWN = '7656988e-ff26-4663-9a4b-5e9cb3505529',
}

export enum TransactionStatusId {
  NEW = 'cbd81669-15f8-49a5-adbc-2d9bbf66cac1',
  IN_PROGRESS = '98c20081-9612-40bd-9264-3e69b16e7111',
  SUCCESS = '04e725b9-feff-4a8f-b0a4-d2f679eb55ce',
  ERROR = '80962a9b-429e-4a08-bbf2-635807877d58',
  NOT_COMPLETED = '7b7180c5-8be8-4f15-92ec-724ccec43417',
}

export type ObjectType = {
  id: ObjectTypeId;
  code: string;
  name: string;
};

export type ObjectOperationCode = PermitObjectOperation | GmpObjectOperation;
export type ObjectStatusCode =
  | PermitObjectStatus
  | GmpObjectStatus
  | ConsignmentCirculationObjectStatus;

export type ObjectOperation = {
  id: string;
  code: ObjectOperationCode;
  name: string;
  objectType: ObjectType;
};

export type ObjectStatus = {
  id: string;
  code: ObjectStatusCode;
  name: string;
  objectType: ObjectType;
};

export type ObjectOperationParams = Partial<{
  objectTypeId: ObjectTypeId;
  withObjectStatusModel: boolean;
  currentObjectStatusId: string;
  permissionList: string[];
}>;

export type ObjectStatusParams = Partial<{
  objectTypeId: ObjectTypeId;
  objectOperationId: string;
  currentObjectStatusId: string;
  withObjectStatusModel: boolean;
  codeList: ObjectStatusCode[];
  excludeCodeList: ObjectStatusCode[];
}>;

export type CerberusIntegrationStatusDictOption = {
  value: CerberusIntegrationStatus;
  label: string;
};
